<template>
  <Teleport to="body">
    <div class="detail-overlay" @click.self="close">
      <div class="detail-panel" role="dialog" aria-modal="true">
        <button
          ref="closeBtn"
          type="button"
          class="detail-close"
          :aria-label="$t('imageDetail.close')"
          @click="close"
        >×</button>

        <div class="detail-header">
          <h2>{{ headerText }}</h2>
          <span class="status-badge" :class="`status-${record.status}`">
            {{ $t(`common.statuses.${record.status}`) }}
          </span>
        </div>

        <div class="detail-body">
          <!-- Image preview -->
          <div class="detail-image">
            <img
              :src="imageUrl(record)"
              :alt="altTextFor(record, currentLocale)"
              @error="onImgError($event, record, currentLocale)"
            />
          </div>

          <!-- Editable fields stack -->
          <div class="detail-fields">
            <!-- Read-only metadata rows -->
            <div class="meta-block">
              <div class="meta-row">
                <span class="meta-label">{{ $t('imageDetail.fields.category') }}</span>
                <span>{{ $t(`common.categories.${record.category_id}`) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">{{ $t('imageDetail.fields.license') }}</span>
                <span>{{ $t(`imageUploader.licenses.${record.license_id}`) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">{{ $t('imageDetail.fields.imageType') }}</span>
                <span>{{ $t(`imageUploader.imageTypes.${record.image_type_id}`) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">{{ $t('imageDetail.fields.uploadedBy') }}</span>
                <span>{{ record.uploaded_by || '—' }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">{{ $t('imageDetail.fields.uploaded') }}</span>
                <span>{{ formatDate(record.uploaded_at) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">{{ $t('imageDetail.fields.updated') }}</span>
                <span>{{ formatDate(record.updated_at) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">{{ $t('imageDetail.fields.file') }}</span>
                <span>{{ fileMeta }}</span>
              </div>
            </div>

            <!-- Labels fieldset -->
            <fieldset class="form-fieldset">
              <legend>{{ $t('imageDetail.labels.legend') }}</legend>
              <div class="form-row two-col">
                <div class="form-field" v-for="lang in LANGS" :key="`label-${lang}`">
                  <label :for="`detail-label-${lang}`">{{ lang.toUpperCase() }}</label>
                  <input
                    :id="`detail-label-${lang}`"
                    type="text"
                    v-model="form[`label_${lang}`]"
                  />
                </div>
              </div>
            </fieldset>

            <!-- Alt text fieldset -->
            <fieldset class="form-fieldset">
              <legend>{{ $t('imageDetail.altText.legend') }}</legend>
              <div class="form-row two-col">
                <div class="form-field" v-for="lang in LANGS" :key="`alt-${lang}`">
                  <label :for="`detail-alt-${lang}`">{{ lang.toUpperCase() }}</label>
                  <input
                    :id="`detail-alt-${lang}`"
                    type="text"
                    v-model="form[`alt_text_${lang}`]"
                  />
                </div>
              </div>
            </fieldset>

            <!-- Category / License / Image type selects -->
            <div class="form-row three-col">
              <div class="form-field">
                <label for="detail-category">{{ $t('imageDetail.selects.categoryLabel') }}</label>
                <select id="detail-category" v-model.number="form.category_id">
                  <option v-for="id in CATEGORY_IDS" :key="id" :value="id">
                    {{ $t(`common.categories.${id}`) }}
                  </option>
                </select>
              </div>
              <div class="form-field">
                <label for="detail-license">{{ $t('imageDetail.selects.licenseLabel') }}</label>
                <select id="detail-license" v-model.number="form.license_id">
                  <option v-for="id in LICENSE_IDS" :key="id" :value="id">
                    {{ $t(`imageUploader.licenses.${id}`) }}
                  </option>
                </select>
              </div>
              <div class="form-field">
                <label for="detail-image-type">{{ $t('imageDetail.selects.imageTypeLabel') }}</label>
                <select id="detail-image-type" v-model.number="form.image_type_id">
                  <option v-for="id in IMAGE_TYPE_IDS" :key="id" :value="id">
                    {{ $t(`imageUploader.imageTypes.${id}`) }}
                  </option>
                </select>
              </div>
            </div>

            <div class="save-row">
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="!isDirty"
                @click="onSave"
              >
                {{ $t('imageDetail.saveChanges') }}
              </button>
            </div>

            <!-- Curator notes -->
            <fieldset class="form-fieldset">
              <legend>{{ $t('imageDetail.curatorNotes.legend') }}</legend>
              <p v-if="!record.curator_notes" class="hint">{{ $t('imageDetail.curatorNotes.noNotes') }}</p>
              <pre v-else class="notes-existing">{{ record.curator_notes }}</pre>
              <textarea
                rows="2"
                v-model="newNote"
                :placeholder="$t('imageDetail.curatorNotes.placeholder')"
              ></textarea>
              <div class="save-row">
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="!hasNoteContent"
                  @click="onAppendNote"
                >
                  {{ $t('imageDetail.curatorNotes.append') }}
                </button>
              </div>
            </fieldset>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="detail-actions">
          <button type="button" class="btn btn-approve" @click="onApprove">
            {{ $t('imageDetail.actions.approve') }}
          </button>
          <button type="button" class="btn btn-reject" @click="onReject">
            {{ $t('imageDetail.actions.reject') }}
          </button>
          <button type="button" class="btn btn-archive" @click="onArchive">
            {{ $t('imageDetail.actions.archive') }}
          </button>
          <button type="button" class="btn btn-secondary" @click="close">
            {{ $t('imageDetail.actions.close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import i18n from '@/i18n';
import {
  imageUrl,
  pickLocaleString,
  altTextFor,
  onImgError,
} from '@/services/imageHelpers';

const LANGS = ['en', 'de', 'fr', 'it'];
const CATEGORY_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const LICENSE_IDS = [1, 2, 3, 4, 5, 6, 7, 8];
const IMAGE_TYPE_IDS = [1, 2, 3, 4, 5];

// Editable field names — change in any of these toggles the Save button.
const EDITABLE_FIELDS = [
  'label_en', 'label_de', 'label_fr', 'label_it',
  'alt_text_en', 'alt_text_de', 'alt_text_fr', 'alt_text_it',
  'category_id', 'license_id', 'image_type_id',
];

function formStateFromRecord(record) {
  const f = {};
  for (const key of EDITABLE_FIELDS) {
    const v = record[key];
    // Strings: normalize null to '' for clean v-model. Numbers: keep as-is.
    if (key.startsWith('label_') || key.startsWith('alt_text_')) {
      f[key] = v == null ? '' : v;
    } else {
      f[key] = v;
    }
  }
  return f;
}

export default {
  name: 'ImageDetail',
  props: {
    record: { type: Object, required: true },
  },
  emits: ['close'],
  data() {
    return {
      form: formStateFromRecord(this.record),
      newNote: '',
      LANGS,
      CATEGORY_IDS,
      LICENSE_IDS,
      IMAGE_TYPE_IDS,
    };
  },
  computed: {
    currentLocale() {
      return i18n.global.locale.value;
    },
    headerText() {
      const cardId = `#${String(this.record.id).padStart(4, '0')}`;
      const label = pickLocaleString(this.record, 'label', this.currentLocale)
        || this.$t('imageBrowser.noLabel');
      return `${cardId} — ${label}`;
    },
    fileMeta() {
      const r = this.record;
      const parts = [];
      if (r.mime_type) parts.push(r.mime_type);
      if (r.width_px && r.height_px) parts.push(`${r.width_px}×${r.height_px}`);
      if (r.file_size_bytes != null) parts.push(this.formatBytes(r.file_size_bytes));
      return parts.join(' · ') || '—';
    },
    isDirty() {
      return EDITABLE_FIELDS.some(key => {
        const original = this.record[key] == null ? '' : this.record[key];
        const current = this.form[key] == null ? '' : this.form[key];
        return original !== current;
      });
    },
    hasNoteContent() {
      return this.newNote.trim().length > 0;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.$nextTick(() => {
      if (this.$refs.closeBtn) this.$refs.closeBtn.focus();
    });
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    imageUrl,
    altTextFor,
    onImgError,
    close() {
      this.$emit('close');
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    },
    formatDate(iso) {
      if (!iso) return '—';
      try {
        return new Intl.DateTimeFormat('de-CH', {
          dateStyle: 'short',
          timeStyle: 'medium',
        }).format(new Date(iso));
      } catch {
        return iso;
      }
    },
    formatBytes(n) {
      if (n == null) return '';
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    },
    onSave() {
      // TODO: services/imageApi.js → updateImage(this.record.id, this.form)
      // eslint-disable-next-line no-console
      console.log('TODO: save metadata', { id: this.record.id, changes: { ...this.form } });
    },
    onAppendNote() {
      // TODO: services/imageApi.js → appendCuratorNote(this.record.id, this.newNote)
      // eslint-disable-next-line no-console
      console.log('TODO: append curator note', { id: this.record.id, note: this.newNote });
    },
    onApprove() {
      // TODO: services/imageApi.js → updateImage(id, { status: 'APPROVED' })
      // eslint-disable-next-line no-console
      console.log('TODO: approve', this.record.id);
    },
    onReject() {
      // TODO: services/imageApi.js → updateImage(id, { status: 'REJECTED' })
      // eslint-disable-next-line no-console
      console.log('TODO: reject', this.record.id);
    },
    onArchive() {
      // TODO: services/imageApi.js → updateImage(id, { status: 'ARCHIVED' })
      // eslint-disable-next-line no-console
      console.log('TODO: archive', this.record.id);
    },
  },
};
</script>

<style scoped>
.detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.detail-panel {
  position: relative;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  max-width: 960px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px 28px 20px 28px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.detail-close:hover { color: #2c3e50; background: #f1f3f5; }
.detail-close:focus-visible { outline: 2px solid #42b983; outline-offset: 2px; }

/* Header */

.detail-header {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-right: 40px; /* room for close button */
}

.detail-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

/* Status badge — same palette as Image Browser */

.status-badge {
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.3px;
}
.status-DRAFT    { background: #E8E8E8; color: #555; }
.status-PENDING  { background: #FFF3CD; color: #856404; }
.status-APPROVED { background: #D4EDDA; color: #155724; }
.status-REJECTED { background: #F8D7DA; color: #721C24; }
.status-ARCHIVED { background: #E2E3E5; color: #383D41; }

/* Body */

.detail-body {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.detail-image {
  flex: 0 0 360px;
  width: 360px;
  height: 360px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.detail-fields {
  flex: 1 1 360px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Read-only meta rows */

.meta-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 14px;
  background: #fdfdfd;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 4px 0;
  font-size: 13px;
}

.meta-label {
  color: #6c757d;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  flex: 0 0 auto;
}

.meta-row > span:last-child {
  color: #2c3e50;
  text-align: right;
  word-break: break-word;
}

/* Form rows / fields (label-above-control) */

.form-row {
  display: grid;
  gap: 12px;
}
.form-row.two-col { grid-template-columns: repeat(2, 1fr); }
.form-row.three-col { grid-template-columns: repeat(3, 1fr); }

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.form-field select,
.form-field input[type="text"] {
  font-family: inherit;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  color: #2c3e50;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  height: 38px;
  box-sizing: border-box;
  width: 100%;
}

.form-field select:focus,
.form-field input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.18);
}

/* Fieldsets */

.form-fieldset {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 14px 14px 14px;
  margin: 0;
}

.form-fieldset legend {
  padding: 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.form-fieldset textarea {
  font-family: inherit;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  color: #2c3e50;
  width: 100%;
  resize: vertical;
  min-height: 50px;
  box-sizing: border-box;
  margin-top: 8px;
}

.form-fieldset textarea:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.18);
}

.hint {
  color: #6c757d;
  font-size: 12px;
  margin: 4px 0;
  font-style: italic;
}

.notes-existing {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 4px 0 8px 0;
  font-family: inherit;
  font-size: 13px;
  color: #2c3e50;
  white-space: pre-wrap;
  word-break: break-word;
}

.save-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

/* Buttons */

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  letter-spacing: 0.3px;
}
.btn:disabled { cursor: not-allowed; opacity: 0.5; }

.btn-secondary {
  background: #fff;
  color: #2c3e50;
  border: 1px solid #ced4da;
}
.btn-secondary:hover:not(:disabled) { background: #f1f3f5; }

.btn-approve { background: #2e7d32; color: #fff; }
.btn-approve:hover:not(:disabled) { background: #1b5e20; }

.btn-reject { background: #ed6c02; color: #fff; }
.btn-reject:hover:not(:disabled) { background: #c95a00; }

.btn-archive { background: #6c757d; color: #fff; }
.btn-archive:hover:not(:disabled) { background: #545b62; }

/* Action row at bottom */

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 1px solid #e9ecef;
  padding-top: 16px;
}

/* Responsive */

@media (max-width: 700px) {
  .detail-overlay { padding: 8px; }
  .detail-panel { padding: 20px 18px 16px 18px; max-height: 95vh; }
  .detail-body { flex-direction: column; gap: 16px; }
  .detail-image {
    flex: 0 0 auto;
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
  }
  .detail-fields { flex: 1 1 100%; min-width: 0; }
  .form-row.two-col,
  .form-row.three-col { grid-template-columns: 1fr; }
  .detail-actions { flex-direction: column-reverse; }
  .detail-actions .btn { width: 100%; }
}
</style>
